import { supabase } from './supabase';
import type { ProfileShare, ProfileShareSummary, ProfileSnapshot, ShareableField } from '../types';

const TABLE = 'pass_profile_shares';

export const profileShareService = {
  async createShare(params: {
    userId: string;
    selectedFields: ShareableField[];
    snapshot: ProfileSnapshot;
    expiresInDays?: number;
  }): Promise<{ data: ProfileShare | null; error: string | null }> {
    const token = crypto.randomUUID().replace(/-/g, '');
    const days = params.expiresInDays && params.expiresInDays > 0 ? params.expiresInDays : 7;
    const expiresAt = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();

    const { data, error } = await supabase
      .from(TABLE)
      .insert({
        token,
        user_id: params.userId,
        selected_fields: params.selectedFields,
        profile_snapshot: params.snapshot,
        expires_at: expiresAt,
      })
      .select()
      .single();

    if (error) return { data: null, error: error.message };
    return { data: data as ProfileShare, error: null };
  },

  async getShareByToken(token: string): Promise<{ data: ProfileShare | null; error: string | null }> {
    const { data, error } = await supabase
      .from(TABLE)
      .select('*')
      .eq('token', token)
      .gt('expires_at', new Date().toISOString())
      .single();

    if (error) return { data: null, error: error.message };
    return { data: data as ProfileShare, error: null };
  },

  async getUserShares(userId: string): Promise<{ data: ProfileShareSummary[]; error: string | null }> {
    const { data, error } = await supabase
      .from(TABLE)
      .select('id, token, selected_fields, expires_at, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) return { data: [], error: error.message };
    return { data: (data ?? []) as ProfileShareSummary[], error: null };
  },

  async deleteShare(token: string): Promise<{ error: string | null }> {
    const { error } = await supabase
      .from(TABLE)
      .delete()
      .eq('token', token);

    return { error: error?.message ?? null };
  },

  async updateShareExpiry(
    token: string,
    expiresInDays: number,
  ): Promise<{ expiresAt: string | null; error: string | null }> {
    const days = expiresInDays > 0 ? expiresInDays : 7;
    const expiresAt = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();

    // Avoid .select()/.single() — RLS often blocks RETURNING and PostgREST responds 406.
    const { error } = await supabase
      .from(TABLE)
      .update({ expires_at: expiresAt })
      .eq('token', token);

    if (error) return { expiresAt: null, error: error.message };
    return { expiresAt, error: null };
  },
};
